import { forwardRef, useRef, useState } from 'react'
import { merge } from 'remeda'
import { FreeMode, Mousewheel, Navigation, type Swiper } from 'swiper'
import { SwiperSlide } from 'swiper/react'
import { useEventListener } from 'usehooks-ts'
import { ApparelProduct } from '@components/ApparelProduct/ApparelProduct'
import { dataTestIds } from '@velasca/constants'
import { NavigationButton } from '@velasca/ui/navigation-button'
import {
  Container,
  HeadingsContainer,
  LeftArrowContainer,
  RightArrowContainer,
  StyledSwiper,
  SubTitle,
  Title,
} from './ProductsSlider.styles'
import type { ProductsSliderProps } from './ProductsSlider.types'

const getSpaceBetween = (size: string) => {
  switch (size) {
    case 'lg': {
      return {
        desktop: 15,
        mobile: 6,
      }
    }
    case 'xs': {
      return { desktop: 8, mobile: 6 }
    }
    default: {
      return { desktop: 8, mobile: 6 }
    }
  }
}

export const ProductsSlider = forwardRef<HTMLDivElement, ProductsSliderProps>(
  (props, sliderRef) => {
    const {
      title,
      subTitle,
      onQuickAdd,
      loadingSlides = 6,
      showProductType = false,
      arrowVisible = false,
      className = '',
      sliderStyles,
      slidesSize = 'xs',
      showProductReviewBadge,
      breakpoints,
      loop = false,
      loading = false,
      products,
      onAddToCart,
      dataTestId,
    } = props
    const navigationPrevRef = useRef(null)
    const navigationNextRef = useRef(null)
    const [slider, setSlider] = useState<Swiper | undefined>()

    const navigate = (e: KeyboardEvent) => {
      switch (e.code) {
        case 'ArrowRight': {
          slider?.slideNext()
          break
        }
        case 'ArrowLeft': {
          slider?.slidePrev()
          break
        }
        default: {
          return
        }
      }
    }

    useEventListener('keydown', navigate)

    return (
      <Container
        ref={sliderRef}
        className={`${className} ${slidesSize}`}
        data-testid={dataTestIds.GENERIC_PRODUCTS_SLIDER}
      >
        {(title || subTitle) && (
          <HeadingsContainer>
            {title && <Title>{title}</Title>}
            {subTitle && (
              <SubTitle dangerouslySetInnerHTML={{ __html: subTitle }} />
            )}
          </HeadingsContainer>
        )}
        <StyledSwiper
          centerInsufficientSlides
          freeMode
          loop={loop}
          style={sliderStyles}
          slidesPerView="auto"
          mousewheel={{ forceToAxis: true }}
          spaceBetween={getSpaceBetween(slidesSize).mobile}
          modules={[Navigation, FreeMode, Mousewheel]}
          data-testid={dataTestId}
          navigation={{
            prevEl: navigationPrevRef.current,
            nextEl: navigationNextRef.current,
          }}
          breakpoints={merge(breakpoints, {
            992: {
              spaceBetween: getSpaceBetween(slidesSize).desktop,
            },
          })}
          onSwiper={setSlider}
          onBeforeInit={(swiper) => {
            const { navigation } = swiper.params

            if (navigation && typeof navigation === 'object') {
              navigation.prevEl = navigationPrevRef.current
              navigation.nextEl = navigationNextRef.current
            }
          }}
        >
          {arrowVisible && (
            <LeftArrowContainer>
              <NavigationButton
                buttonType="prev"
                onClick={() => {
                  slider?.slidePrev()
                }}
              />
            </LeftArrowContainer>
          )}
          {loading
            ? [...Array(loadingSlides).keys()].map((id) => (
                <SwiperSlide key={id}>
                  <ApparelProduct loading showSeason size={slidesSize} />
                </SwiperSlide>
              ))
            : products?.map(
                ({
                  id,
                  productType,
                  title: productTitle,
                  variants,
                  sizes,
                  isGiftCard,
                  collectionBrandID,
                }) => {
                  return (
                    <SwiperSlide key={id}>
                      <ApparelProduct
                        showSeason
                        isQuickAdd
                        showProductType={showProductType}
                        productType={productType}
                        title={productTitle}
                        variants={variants}
                        defaultSelected={0}
                        size={slidesSize}
                        sizes={sizes}
                        isGiftCard={isGiftCard}
                        showReviewBadge={showProductReviewBadge}
                        collectionBrandID={collectionBrandID}
                        onQuickAdd={onQuickAdd}
                        onAddToCart={onAddToCart}
                      />
                    </SwiperSlide>
                  )
                },
              )}
          {arrowVisible && (
            <RightArrowContainer>
              <NavigationButton
                buttonType="next"
                onClick={() => {
                  slider?.slideNext()
                }}
              />
            </RightArrowContainer>
          )}
        </StyledSwiper>
      </Container>
    )
  },
)
